import axios from 'axios';
import { getToken } from '@/composables/get-token';
import handleError from '@/composables/handle-error';
import store from '@/store/';
import { isString } from '@/utils/inspect';

let requestsPending = 0;
let loadingName = 'loading';
const actionScope = `loader`;

const req = {
	pending: () => {
		requestsPending++;
		store.dispatch(`${actionScope}/show`, loadingName);
	},
	done: () => {
		requestsPending--;

		if (requestsPending <= 0) {
			store.dispatch(`${actionScope}/hide`, loadingName);
		}
	}
};

class AxiosHelper {
	constructor(opts) {
		this.options = opts;
		this.pendingRequests = {};
	}

	get apiClient() {
		let body = {};
		if (!this.options.action) {
			body = {
				baseURL: this.options.url,
				headers: {
					Accept: 'application/json',
					Authorization: `Bearer ${getToken()}`
				}
			};
		}

		if (this.options.action === 'upload') {
			body = {
				baseURL: this.options.url,
				headers: {
					Accept: 'application/json',
					Authorization: `Bearer ${getToken()}`,
					'Content-Type': 'multipart/form-data'
				}
			};
		}
		if (this.options.action === 'download') {
			body = {
				baseURL: this.options.url,
				headers: {
					Accept: this.options.contentType,
					Authorization: `Bearer ${getToken()}`
				},
				responseType: 'arraybuffer'
			};
		}
		// handling timeout in the request
		if (this.options.timeout) {
			const { timeout } = this.options;
			body.timeout = timeout;
		}
		this._apiClient = axios.create(body);

		this._apiClient.interceptors.request.use(
			config => {
				this.setLoadingName(config);
				req.pending();

				const requestId = `${config.method}:${config.url}`;

				if (this.pendingRequests[requestId]) {
					this.pendingRequests[requestId](
						'Request canceled because of new request:499'
					);
				}
				const cancelTokenSource = axios.CancelToken.source();
				this.pendingRequests[requestId] = cancelTokenSource.cancel;
				config.cancelToken = cancelTokenSource.token;

				return config;
			},
			error => {
				requestsPending--;
				req.done();
				return Promise.reject(error);
			}
		);

		this._apiClient.interceptors.response.use(
			response => {
				req.done();

				let result;
				if (isString(response.data)) {
					result = response.data;
				} else {
					result = response.data || {};
					result.statusCode = response.status || 200;
				}

				return Promise.resolve(result);
			},
			error => {
				req.done();
				handleError(error);
			}
		);

		return this._apiClient;
	}

	async get(route, params) {
		return await this.apiClient.get(route, params);
	}

	async download(route, params) {
		return await this.apiClient.get(route, params);
	}

	async post(route, body) {
		return await this.apiClient.post(route, body);
	}

	async put(route, body) {
		return await this.apiClient.put(route, body);
	}

	async patch(route, body) {
		return await this.apiClient.patch(route, body);
	}

	async delete(route, body) {
		return await this.apiClient.delete(route, body);
	}

	async postFile(route, body) {
		return await this.apiClient.post(route, body);
	}

	setUrl(options) {
		let url = this.baseURL;

		if (!url.startsWith('http') && !url.startsWith('/')) {
			url = '/' + url;
		}

		if (typeof options == 'string') {
			//this is the route
			url = url + options;
			options = { url: url };
		} else {
			//an options object
			url = url + options.url;
			options.url = url;
		}

		return options;
	}

	setLoadingName(config) {
		const url = config.url.split('?');
		loadingName = config.method + url[0].replace(/\//g, '.');
	}
}

export const ApiService = new AxiosHelper({
	url: process.env.VUE_APP_API
});

export const ApiLogSupportService = new AxiosHelper({
	url: process.env.VUE_APP_API,
	timeout: 60000
});

export const ApiLogService = new AxiosHelper({
	url: process.env.VUE_APP_API_LOGS
});

export const ApiLogServiceTimeOut = new AxiosHelper({
	url: process.env.VUE_APP_API_LOGS,
	timeout: 60000
});

export const ApiGlobalService = new AxiosHelper({
	url: process.env.VUE_APP_API_GLOBAL
});

export const UploadService = new AxiosHelper({
	url: process.env.VUE_APP_API,
	action: 'upload'
});
export const DownloadService = new AxiosHelper({
	url: process.env.VUE_APP_API,
	action: 'download'
});
export const DownloadBlob = contentType => {
	if (!contentType) {
		contentType = 'text/plain';
	}
	return new AxiosHelper({
		url: process.env.VUE_APP_API,
		action: 'download',
		contentType: contentType
	});
};
